import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useState, useMemo, useEffect } from 'react';
import { toursFromHurghada, Tour } from '@/mocks/tours';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const CATEGORIES = [
  { key: 'all', label_de: 'Alle Ausflüge', label_en: 'All Excursions' },
  { key: 'kultur', label_de: 'Kultur & Geschichte', label_en: 'Culture & History' },
  { key: 'insel', label_de: 'Inseln & Bootstouren', label_en: 'Islands & Boat Trips' },
  { key: 'wueste', label_de: 'Wüste & Abenteuer', label_en: 'Desert & Adventure' },
  { key: 'stadt', label_de: 'Stadtrundfahrten', label_en: 'City Tours' },
  { key: 'schnorcheln', label_de: 'Schnorcheln & Meer', label_en: 'Snorkeling & Sea' },
];

function getCategory(tour: Tour): string {
  const id = tour.id;
  if (id.includes('luxor') || id.includes('cairo') || id.includes('aswan') || id.includes('dendera') || id.includes('abydos') || id.includes('monastery')) return 'kultur';
  if (id.includes('orange') || id.includes('paradise') || id.includes('private-boat') || id.includes('bay')) return 'insel';
  if (id.includes('quad') || id.includes('buggy') || id.includes('ghost') || id.includes('bahariya')) return 'wueste';
  if (id.includes('stadtrundfahrt') || id.includes('elgouna')) return 'stadt';
  if (id.includes('dolphin') || id.includes('sharm') || id.includes('abu-dabbab')) return 'schnorcheln';
  return 'all';
}

export default function HurghadaTours() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isDe = i18n.language === 'de';
  const [activeCategory, setActiveCategory] = useState('all');
  const [visibleCount, setVisibleCount] = useState(12);
  const [, setCurrencyVersion] = useState(0);

useEffect(() => {
  const handleCurrencyChange = () => {
    setCurrencyVersion((prev) => prev + 1);
  };

  window.addEventListener('currencyChange', handleCurrencyChange);

  return () => {
    window.removeEventListener('currencyChange', handleCurrencyChange);
  };
}, []);

  const filteredTours = useMemo(() => {
    if (activeCategory === 'all') return toursFromHurghada;
    return toursFromHurghada.filter((tour) => getCategory(tour) === activeCategory);
  }, [activeCategory]);

  const visibleTours = filteredTours.slice(0, visibleCount);

  const getDurationBadge = (type: string) => {
  switch (type) {
    case 'half_day':
      return isDe ? 'Halbtagestour' : 'Half-Day Tour';
    case 'full_day':
      return isDe ? 'Tagesausflug' : 'Day Trip';
    case 'multi_day':
      return isDe ? 'Mehrtägig' : 'Multi-Day';
    default:
      return type;
  }
};
  const getCurrencySymbol = () => ((window as any).__CURRENCY__?.symbol) || '\u20AC';
  const getConvertedPrice = (p: number) => Math.round(p * (((window as any).__CURRENCY__?.rate) || 1));

  const getDisplayPrice = (tour: Tour): { label: string; childLabel?: string } => {
  if (!tour.pricing_tiers || tour.pricing_tiers.length === 0) {
    const p = getConvertedPrice(tour.price);
    return {
      label: `${isDe ? 'Ab' : 'From'} ${getCurrencySymbol()}${p}`,
    };
  }

  const prices = tour.pricing_tiers.map((tier) =>
    getConvertedPrice(tier.price)
  );

  const min = Math.min(...prices);

  return {
    label: `${isDe ? 'Ab' : 'From'} ${getCurrencySymbol()}${min}`,
    childLabel:
      tour.child_price != null
        ? `${isDe ? 'Kind ab' : 'Child from'} ${getCurrencySymbol()}${getConvertedPrice(tour.child_price)}`
        : '',
  };
};

  const getCategoryCount = (catKey: string) => {
    if (catKey === 'all') return toursFromHurghada.length;
    return toursFromHurghada.filter((t) => getCategory(t) === catKey).length;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1">
        {/* Hero Banner */}
        <section className="relative pt-28 pb-16 md:pt-36 md:pb-20 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://storage.readdy-site.link/project_files/6489a824-10a8-4047-916b-1e3e62578ec7/9422cd40-60e0-4639-b62e-e588c4702413_compressed_Untitled-design-8.webp"
              alt="Hurghada"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-primary-950/75 via-primary-950/55 to-primary-950/75" />
          </div>
          <div className="relative w-full px-4 md:px-6 lg:px-12 xl:px-20 text-center">
            <div className="inline-flex items-center gap-2 bg-accent-500/20 border border-accent-500/30 rounded-full px-4 py-1.5 mb-5">
              <i className="ri-map-pin-line text-accent-400 text-sm" />
              <span className="text-accent-300 text-xs font-medium tracking-wide uppercase">Hurghada</span>
            </div>
            <h1 className="font-heading text-3xl md:text-5xl font-bold text-background-50 mb-4">
              {isDe ? 'Ausflüge ab Hurghada' : 'Excursions from Hurghada'}
            </h1>
            <p className="text-background-200 text-sm md:text-base max-w-xl mx-auto leading-relaxed">
              {isDe
  ? 'Entdecken Sie Ägyptens schönste Sehenswürdigkeiten mit privaten Ausflügen ab Hurghada, deutschsprachiger Reiseleitung und persönlichem Service.'
  : 'Discover Egypt’s most beautiful sights with private excursions from Hurghada, English-speaking guides and personal service.'
}
            </p>
          </div>
        </section>

        {/* Category Filter */}
        <section className="bg-background-50 border-b border-background-200 sticky top-[97px] z-40">
          <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20">
            <div className="flex items-center gap-1 overflow-x-auto py-3 scrollbar-hide">
              {CATEGORIES.map((cat) => {
                const count = getCategoryCount(cat.key);
                const isActive = activeCategory === cat.key;
                return (
                  <button
                    key={cat.key}
                    onClick={() => { setActiveCategory(cat.key); setVisibleCount(12); }}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      isActive
                        ? 'bg-primary-800 text-background-50'
                        : 'text-foreground-600 hover:text-foreground-800 hover:bg-background-100'
                    }`}
                  >
                    <span>{isDe ? cat.label_de : cat.label_en}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-primary-700/50 text-background-200' : 'bg-background-200 text-foreground-500'}`}>
                      {count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        </section>

        {/* Tours Grid */}
        <section className="bg-background-50 py-8 md:py-12">
          <div className="w-full px-4 md:px-6 lg:px-12 xl:px-20">
            {/* Results Count */}
            <p className="text-sm text-foreground-500 mb-6">
              {filteredTours.length} {isDe ? 'Ausflüge gefunden' : 'excursions found'}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
              {visibleTours.map((tour) => {
                const displayPrice = getDisplayPrice(tour);
                return (
                  <div
                    key={tour.id}
                    className="group bg-background-50 rounded-sm overflow-hidden border border-background-200 hover:border-accent-300 transition-all duration-300 hover:shadow-md"
                  >
                    {/* Image */}
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={tour.image_url}
                        alt={isDe ? tour.title_de : tour.title_en}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute top-3 right-3">
                        <span className="inline-block px-2.5 py-1 bg-primary-800/90 text-background-50 text-xs rounded-sm font-medium">
                          {getDurationBadge(tour.duration_type)}
                        </span>
                      </div>
                      <div className="absolute bottom-3 left-3 flex items-center gap-1 bg-background-50/90 px-2 py-1 rounded-sm text-xs">
                        <i className="ri-star-fill text-accent-500 text-xs" />
                        <span className="font-semibold text-foreground-800">{tour.rating}</span>
                        <span className="text-foreground-500">({tour.reviews_count})</span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-4">
                      <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 line-clamp-2 leading-tight">
                        {isDe ? tour.title_de : tour.title_en}
                      </h3>
                      <p className="text-foreground-500 text-sm mb-4 line-clamp-2 leading-relaxed">
                        {isDe ? tour.description_de : tour.description_en}
                      </p>

                      {/* Pricing Tiers Preview */}
                      {tour.pricing_tiers && tour.pricing_tiers.length > 1 && (
                        <div className="mb-3 flex flex-wrap gap-1.5">
                          {tour.pricing_tiers.slice(0, 3).map((tier) => (
                            <span key={tier.persons} className="inline-block text-[10px] text-foreground-500 bg-background-100 px-1.5 py-0.5 rounded-sm">
                              {tier.persons}: {getCurrencySymbol()}{getConvertedPrice(tier.price)}
                            </span>
                          ))}
                          {tour.pricing_tiers.length > 3 && (
                            <span className="inline-block text-[10px] text-foreground-400 px-1 py-0.5">+{tour.pricing_tiers.length - 3} mehr</span>
                          )}
                        </div>
                      )}

                      {/* Price & CTA */}
                      <div className="flex items-center justify-between pt-3 border-t border-background-200">
                        <div>
                        
                          <div className="flex items-baseline gap-1">
                            <span className="text-xl font-bold text-accent-600">
                              {displayPrice.label}
                            </span>
                            <span className="text-xs text-foreground-500">
  {isDe ? 'pro Person' : 'per person'}
</span>
                          </div>
                          {displayPrice.childLabel && (
                            <span className="text-[10px] text-foreground-400">{displayPrice.childLabel}</span>
                          )}
                        </div>
                        <button
                          onClick={() => navigate(`/tour/${tour.id}`)}
                          className="px-4 py-2.5 bg-primary-500 text-background-50 text-sm font-medium rounded-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                         {isDe ? 'Details ansehen' : 'See Details'}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Load More */}
            {visibleCount < filteredTours.length && (
              <div className="text-center mt-10">
                <button
                  onClick={() => setVisibleCount((prev) => prev + 12)}
                  className="inline-flex items-center gap-2 px-8 py-3 border-2 border-primary-600 text-primary-600 text-sm font-semibold rounded-sm hover:bg-primary-600 hover:text-background-50 transition-all cursor-pointer whitespace-nowrap"
                >
                  {isDe ? 'Weitere Ausflüge laden' : 'Load More Excursions'}
                  <i className="ri-arrow-down-line" />
                </button>
              </div>
            )}

            {filteredTours.length === 0 && (
              <div className="text-center py-16">
                <i className="ri-search-line text-4xl text-foreground-300 mb-4 block" />
                <p className="text-foreground-500">
                  {isDe ? 'Keine Ausflüge in dieser Kategorie gefunden.' : 'No excursions found in this category.'}
                </p>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}